import { memo, useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import dayjs from 'dayjs';
import { FEATURE_NAMES, FLOW2_CATEGORY_GOALS, TermTabType } from '../../../common/constants';
import { useFundingTermScope } from '../../../common/hooks';
import { getGoalPriorityProbability } from '../../../common/helper';
import { useAppSelector } from '../../../common/hooks';
import { GoalTypes } from '../../../common/types/clients-entity';
import { ContributionScheduleAccount } from '../../molecules/ContributionScheduleTable/ContributionScheduleTable';
import { FundingNavigatorGoalCard } from '../FundingNavigatorGoalCard';
import { useGetPortfolioCompositionData } from '../PortfolioComposition/useGetPortfolioCompositionData';
import { GoeConfigType } from '../../../features/global/globalSlice';
import { selectEwsRequests, selectEwsResponses } from '../../../features/ews';
import { checkFFEnabled, formatCurrency } from '../../../utils';

export type GoalCardWrapperProps = {
    g: any;
    selectedTermIndex: TermTabType;
    goeConfig: GoeConfigType;
    portfolioCompositionSource: any[];
    allMarketPortfolios: any[];
};

const GoalCardWrapperComponent = ({
    g,
    selectedTermIndex,
    goeConfig,
    portfolioCompositionSource,
    allMarketPortfolios
}: GoalCardWrapperProps) => {
    const { durationKey } = useFundingTermScope(selectedTermIndex);
    const goalId = g.goalId;

    // Read only this goal's slice of the term entry. `.find()` extracts stable per-goal
    // references so a stream tick for another goal leaves this card's selector output unchanged.
    const { ewsArray, contributionSchedules, runpipeResponse, runpipeGoePayload, goalProfileList } = useAppSelector(
        (state) => {
            const termEntry = selectEwsResponses(state)?.[durationKey];
            const request = selectEwsRequests(state)?.[durationKey];
            const body = termEntry?.response?.body;
            return {
                ewsArray: body?.ews,
                contributionSchedules: body?.contributionSchedules,
                runpipeResponse: body?.runPipeResponses?.find((r: any) => r.goalId === goalId),
                runpipeGoePayload: termEntry?.goePayload?.runpipe?.find((r: any) => r.goalId === goalId)
                    ?.runpipe_request,
                goalProfileList: request?.goalProfileList
            };
        },
        shallowEqual
    );

    const accounts = useAppSelector((state) => state.adviceClient?.accounts);
    const getAccountNameFromId = useCallback(
        (accountId: string) => {
            const account: any = [...(accounts?.incomeSources ?? []), ...(accounts?.investmentsSources ?? [])]?.find(
                (acc: any) => acc.accountId === accountId
            );
            return account?.accountName || account?.sourceName || '-';
        },
        [accounts]
    );

    const fundingSources = useMemo(() => {
        if (!ewsArray) return [] as any[];
        return ewsArray
            .filter((source: any) => source.goalId === goalId)
            .map((source: any) => ({
                name: getAccountNameFromId(source.accountId),
                currentAllocatedBalance: source.lumpsumAmount ? formatCurrency(source.lumpsumAmount) : '-',
                nextContribution: source.firstContributionAmount ? formatCurrency(source.firstContributionAmount) : '-',
                nextContributionDate: source.firstContributionDate
                    ? dayjs(source.firstContributionDate, 'DD-MM-YYYY').format('MMM YYYY')
                    : '-'
            }));
    }, [ewsArray, goalId, getAccountNameFromId]);

    const isFullScheduleEnabled = checkFFEnabled(FEATURE_NAMES['FUNDINGNAV_FULLSCHEDULE_FE_AEX-3368']);
    const contributionAccounts = useMemo(() => {
        if (!isFullScheduleEnabled || !contributionSchedules?.length) return [] as ContributionScheduleAccount[];
        const schedule = contributionSchedules.find((s: any) => s.goalId === goalId);
        return (schedule?.accounts ?? []).map((account: any) => ({
            name: getAccountNameFromId(account.accountId),
            dateValueList: account.dateValueList ?? []
        }));
    }, [contributionSchedules, isFullScheduleEnabled, goalId, getAccountNameFromId]);

    // A goal present in the term's ews request but without a runpipe response yet is still loading.
    const isRunpipeLoading = useMemo(
        () => Boolean(goalProfileList?.some((gp: any) => gp?.goalId === goalId)) && !runpipeResponse,
        [goalProfileList, goalId, runpipeResponse]
    );

    const gSpecs = FLOW2_CATEGORY_GOALS.find((goal) => g.data.type === goal.type);
    const selectedGoalAnalysisReport = runpipeResponse?.response?.body?.analysisReport;
    const goalPriorityValue = goeConfig?.goalPriorityProbabilities;
    const thresholdValue = Math.round(getGoalPriorityProbability(goalPriorityValue, g?.data?.['goalPriority']) * 100);
    const recommendedPortfolioId = selectedGoalAnalysisReport?.recommendedPortfolioId;
    const { doughnutChartData, groupedPortfolio } = useGetPortfolioCompositionData(
        recommendedPortfolioId,
        portfolioCompositionSource.length ? portfolioCompositionSource : allMarketPortfolios,
        true
    );
    const fundError = runpipeResponse?.noEligibleFundings;
    return (
        <FundingNavigatorGoalCard
            key={`${selectedTermIndex}-${goalId}`}
            goalName={g.data.goalName}
            goalType={g.data.type}
            priority={g.data.goalPriority}
            iconType={g.data.type}
            iconColor={gSpecs?.color}
            iconBgColor={gSpecs?.backgroundColor}
            doughnutChartData={doughnutChartData}
            groupedPortfolio={groupedPortfolio}
            isOnTarget={Math.round(Number(selectedGoalAnalysisReport?.currentGoalProbability) * 100) >= thresholdValue}
            target={Math.round(Number(selectedGoalAnalysisReport?.currentGoalProbability) * 100)}
            fundingSources={fundingSources}
            amount={g.data.type === GoalTypes.Retirement ? g.data.targetedRetirementIncome : g.data.goalAmount}
            targetDate={g.data.targetDate}
            planStartRetirementDate={
                g.data.type !== GoalTypes.Wealth ? g.data.planStartRetirement || g.data.incomeStartDate : undefined
            }
            fundError={fundError}
            contributionAccounts={contributionAccounts}
            isRunpipeLoading={isRunpipeLoading}
            isRunpipeSuccess={runpipeResponse?.success}
            runpipe_goePayLoad={runpipeGoePayload}
            goalId={goalId}
            durationKey={durationKey}
        />
    );
};

export const GoalCardWrapper = memo(GoalCardWrapperComponent);
